import type { AddressOut } from '@/lib/api/types';

/** The sentinel the picker uses for "a new address". */
export const NEW_ADDRESS = 'new';

/** The two screens a new address is built on. See DeliveryAddress. */
export type AddressPhase = 'point' | 'details';

/** The shop ships in one state. When the map cannot name it, this is it. */
export const HOME_STATE = 'Kerala';

/** The names every delivery app offers, in the order they offer them. */
export const LABEL_PRESETS = ['Home', 'Work'] as const;

/**
 * What a form hands over when it is valid: the typed lines merged with what
 * the map said, so the caller posts it and never reasons about which field
 * came from where.
 */
export interface DeliverySubmit {
  name: string;
  phone: string;
  line1: string;
  landmark: string | null;
  label: string | null;
  locality: string | null;
  city: string;
  state: string;
  pincode: string;
}

/** A saved address, as the thing a form would have produced for it. */
export function fromSaved(entry: AddressOut): DeliverySubmit {
  return {
    name: entry.name,
    phone: entry.phone,
    line1: entry.line1,
    landmark: entry.landmark ?? null,
    label: entry.label ?? null,
    locality: entry.locality ?? null,
    city: entry.city,
    state: entry.state,
    pincode: entry.pincode,
  };
}

/** One line for a picker row: door, area, PIN. */
export function savedSummary(entry: AddressOut): string {
  return [entry.line1, entry.locality ?? entry.city, entry.pincode].filter(Boolean).join(', ');
}

/** The lines a rider reads, in the order they read them. */
export function savedLines(entry: AddressOut): string {
  return [entry.line1, entry.landmark, entry.locality, `${entry.city} ${entry.pincode}`]
    .filter(Boolean)
    .join(', ');
}

/** True when another address already goes by this name (case-insensitive). */
export function labelTaken(label: string, takenLabels: readonly string[]): boolean {
  const wanted = label.trim().toLowerCase();
  return wanted !== '' && takenLabels.some((used) => used.trim().toLowerCase() === wanted);
}

/** The message both forms and the server use for it. */
export function labelTakenMessage(label: string): string {
  return `You already have an address called ${label.trim()}. Pick another name.`;
}
